import {
  Button,
  Divider,
  Icons,
  Modal,
  ModalClose,
  ModalDialog,
  Stack,
  Step,
  StepIndicator,
  Stepper,
  Typography,
} from '@healthinal/ui';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import {
  getGetClinicalReportByPatientQueryKey,
  getGetClinicalReportByPatientQueryOptions,
  useDeleteClinicalReport,
  useUpdateClinicalReport,
} from '../../../../../api/generated/diagnosis-controller/diagnosis-controller.ts';
import {
  AddressContactDto,
  AddressDto,
  ClinicalReportDto,
  ClinicalReportDtoOrgansItem,
  ClinicalReportMaterialDto,
  ClinicalReportMaterialDtoClinicalTestMaterial,
  ClinicalReportMaterialResultDto,
  ClinicalReportMaterialResultDtoClinicalDetectionMethod,
  ClinicalReportMaterialResultDtoTestResult,
  PatientAddressDtoReferenceType,
} from '../../../../../api/generated/model';
import { getCurrentDate } from '../../../../../utils/date.ts';
import { blobToBase64 } from '../../../../../utils/file.ts';
import { getStringOrUndefined } from '../../../../../utils/string.ts';
import { DeleteConfirmationDialog } from '../../../../components/DeleteConfirmationDialog.tsx';
import { ClinicalReportDiagnosisForm } from './ClinicalReportDiagnosisForm.tsx';
import { ClinicalReportDoctorsForm } from './ClinicalReportDoctorsForm.tsx';

interface MaterialFormValues {
  clinicalTestMaterial: ClinicalReportMaterialDtoClinicalTestMaterial;
  otherMaterial: string | null;
  materialResults: MaterialResultFormValues[];
}

interface MaterialResultFormValues {
  clinicalDetectionMethod: ClinicalReportMaterialResultDtoClinicalDetectionMethod;
  testResult: ClinicalReportMaterialResultDtoTestResult | null;
}

interface FormValues {
  affectedOrgans: ClinicalReportDtoOrgansItem[];
  otherOrgan: string;
  laboratoryAddress: AddressDto | null;
  laboratoryContact: AddressContactDto | null;
  file: File | null;
  materials: MaterialFormValues[];
  doctorAddress: AddressDto | null;
  doctorContact: AddressContactDto | null;
  treatingDoctorAddress: AddressDto | null;
  treatingDoctorContact: AddressContactDto | null;
  cantonDoctorAddress: AddressDto | null;
  cantonDoctorContact: AddressContactDto | null;
  notes: string;
}

const steps = ['Diagnose & Manifestation', 'Ärzte & Bemerkungen'];

export function EditClinicalReportDialog({
  open,
  onClose,
  currentClinicalReport,
}: {
  open: boolean;
  onClose: () => void;
  currentClinicalReport: ClinicalReportDto;
}) {
  const queryClient = useQueryClient();

  const [activeStep, setActiveStep] = useState(1);
  const [isDeleteClinicalReportConfirmationDialogOpen, setIsDeleteClinicalReportConfirmationDialogOpen] =
    useState(false);

  const formMethods = useForm<FormValues>({
    defaultValues: getDefaultValues(currentClinicalReport),
    mode: 'onChange',
    reValidateMode: 'onChange',
  });

  const handleOnClose = (reason?: string) => {
    if (reason === 'backdropClick') {
      return;
    }
    setActiveStep(1);
    formMethods.reset();
    onClose();
  };

  const updateClinicalReportMutation = useUpdateClinicalReport({
    mutation: {
      onSuccess: () => {
        void queryClient.invalidateQueries(getGetClinicalReportByPatientQueryOptions(currentClinicalReport.patientId));
        handleOnClose();
      },
      onError: () => {
        toast.error('Beim updaten des MKB ist ein Fehler aufgetreten');
      },
    },
  });

  const deleteClinicalReportMutation = useDeleteClinicalReport({
    mutation: {
      onSuccess: () => {
        toast.success('MKB wurde erfolgreich gelöscht');
        void queryClient.invalidateQueries({
          queryKey: getGetClinicalReportByPatientQueryKey(currentClinicalReport.patientId),
        });
      },
      onError: () => toast.error('Beim Löschen des MKB ist ein Fehler aufgetreten'),
    },
  });

  useEffect(() => {
    if (open && currentClinicalReport) {
      formMethods.reset(getDefaultValues(currentClinicalReport));
    }
  }, [open, currentClinicalReport]);

  return (
    <>
      <Modal open={open} onClose={(_, reason) => handleOnClose(reason)}>
        <ModalDialog sx={{ width: 850, overflowY: 'scroll' }}>
          <ModalClose />
          <Typography level={'h4'}>MKB bearbeiten</Typography>
          <Stepper>
            {steps.map((step, index) => (
              <Step
                key={step}
                indicator={
                  <StepIndicator
                    variant={activeStep <= index ? 'soft' : 'solid'}
                    color={activeStep < index ? 'neutral' : 'primary'}>
                    {index + 1}
                  </StepIndicator>
                }>
                {step}
              </Step>
            ))}
          </Stepper>
          <Divider />
          <FormProvider {...formMethods}>
            <form
              onSubmit={formMethods.handleSubmit(async (data) =>
                updateClinicalReportMutation.mutate({
                  clinicalReportId: currentClinicalReport.id,
                  data: {
                    patientId: currentClinicalReport.patientId,
                    laboratoryAddress: data.laboratoryAddress
                      ? {
                          address: data.laboratoryAddress,
                          contact: data.laboratoryContact ?? undefined,
                          from: getCurrentDate(),
                          patientId: currentClinicalReport.patientId,
                          referenceType: PatientAddressDtoReferenceType.CLINICAL_REPORT_LABORATORY,
                        }
                      : undefined,
                    doctorAddress: data.doctorAddress
                      ? {
                          address: data.doctorAddress,
                          contact: data.doctorContact ?? undefined,
                          from: getCurrentDate(),
                          patientId: currentClinicalReport.patientId,
                          referenceType: PatientAddressDtoReferenceType.CLINICAL_REPORT_DOCTOR,
                        }
                      : undefined,
                    treatingDoctorAddress: data.treatingDoctorAddress
                      ? {
                          address: data.treatingDoctorAddress,
                          contact: data.treatingDoctorContact ?? undefined,
                          from: getCurrentDate(),
                          patientId: currentClinicalReport.patientId,
                          referenceType: PatientAddressDtoReferenceType.CLINICAL_REPORT_TREATING_DOCTOR,
                        }
                      : undefined,
                    cantonDoctorAddress: data.cantonDoctorAddress
                      ? {
                          address: data.cantonDoctorAddress,
                          contact: data.cantonDoctorContact ?? undefined,
                          from: getCurrentDate(),
                          patientId: currentClinicalReport.patientId,
                          referenceType: PatientAddressDtoReferenceType.CLINICAL_REPORT_CANTON_DOCTOR,
                        }
                      : undefined,
                    file: data.file
                      ? {
                          fileName: data.file.name,
                          file: await blobToBase64(data.file),
                        }
                      : undefined,
                    materials: data.materials.map((material) => ({
                      clinicalTestMaterial: material.clinicalTestMaterial,
                      otherMaterial: getStringOrUndefined(material.otherMaterial),
                      clinicalReportMaterialResults: material.materialResults.filter(
                        ({ testResult }) => testResult,
                      ) as ClinicalReportMaterialResultDto[],
                    })) as ClinicalReportMaterialDto[],
                    organs: data.affectedOrgans,
                    otherOrgan: getStringOrUndefined(data.otherOrgan),
                    notes: getStringOrUndefined(data.notes),
                  },
                }),
              )}>
              {activeStep === 1 && (
                <ClinicalReportDiagnosisForm
                  affectedOrgans={'affectedOrgans'}
                  otherOrgan={'otherOrgan'}
                  file={'file'}
                  materials={'materials'}
                  fileInputPlaceHolder={currentClinicalReport.fileDetails?.fileName}>
                  <Divider inset={'context'} />
                  <Stack direction={'row'}>
                    <Button
                      color={'danger'}
                      endDecorator={<Icons.Delete />}
                      loading={deleteClinicalReportMutation.isPending}
                      onClick={() => setIsDeleteClinicalReportConfirmationDialogOpen(true)}>
                      Löschen
                    </Button>
                    <Stack flex={1} direction={'row'} justifyContent={'flex-end'} gap={1}>
                      <Button
                        color={'neutral'}
                        onClick={() => {
                          handleOnClose();
                        }}>
                        Abbrechen
                      </Button>
                      <Button
                        onClick={async () => {
                          const isValid = await formMethods.trigger();
                          if (isValid) {
                            setActiveStep(activeStep + 1);
                          }
                        }}>
                        Weiter
                      </Button>
                    </Stack>
                  </Stack>
                </ClinicalReportDiagnosisForm>
              )}
              {activeStep === 2 && (
                <ClinicalReportDoctorsForm
                  doctorAddress={'doctorAddress'}
                  doctorContact={'doctorContact'}
                  treatingDoctorAddress={'treatingDoctorAddress'}
                  treatingDoctorContact={'treatingDoctorContact'}
                  notes={'notes'}>
                  <Divider inset={'context'} />
                  <Stack direction={'row'} justifyContent={'space-between'}>
                    <Button color={'neutral'} onClick={() => setActiveStep(activeStep - 1)}>
                      Zurück
                    </Button>
                    <Button
                      disabled={!formMethods.formState.isDirty}
                      type={'submit'}
                      loading={updateClinicalReportMutation.isPending}>
                      Speichern
                    </Button>
                  </Stack>
                </ClinicalReportDoctorsForm>
              )}
            </form>
          </FormProvider>
        </ModalDialog>
      </Modal>
      <DeleteConfirmationDialog
        open={isDeleteClinicalReportConfirmationDialogOpen}
        onCancel={() => setIsDeleteClinicalReportConfirmationDialogOpen(false)}
        onDelete={() => {
          deleteClinicalReportMutation.mutate({ id: currentClinicalReport.id });
        }}
        modalTitle={'MKB löschen'}>
        <Typography>Ausgewähltes MKB wirklich löschen?</Typography>
      </DeleteConfirmationDialog>
    </>
  );
}

function getDefaultValues(clinicalReport: ClinicalReportDto): FormValues {
  return {
    affectedOrgans: clinicalReport.organs,
    otherOrgan: clinicalReport.otherOrgan ?? '',
    laboratoryAddress: clinicalReport.laboratoryAddress?.address ?? null,
    laboratoryContact: clinicalReport.laboratoryAddress?.contact ?? null,
    file: null,
    materials: [
      {
        clinicalTestMaterial: ClinicalReportMaterialDtoClinicalTestMaterial.SPUTUM,
        otherMaterial: null,
        materialResults: getMaterialResultDefaultValue(
          clinicalReport.materials,
          ClinicalReportMaterialDtoClinicalTestMaterial.SPUTUM,
        ),
      },
      {
        clinicalTestMaterial: ClinicalReportMaterialDtoClinicalTestMaterial.BRONCHIAL_SECRETION,
        otherMaterial: null,
        materialResults: getMaterialResultDefaultValue(
          clinicalReport.materials,
          ClinicalReportMaterialDtoClinicalTestMaterial.BRONCHIAL_SECRETION,
        ),
      },
      {
        clinicalTestMaterial: ClinicalReportMaterialDtoClinicalTestMaterial.OTHER,
        otherMaterial:
          clinicalReport.materials.find(
            (material) => material.clinicalTestMaterial === ClinicalReportMaterialDtoClinicalTestMaterial.OTHER,
          )?.otherMaterial ?? '',
        materialResults: getMaterialResultDefaultValue(
          clinicalReport.materials,
          ClinicalReportMaterialDtoClinicalTestMaterial.OTHER,
        ),
      },
    ],
    doctorAddress: clinicalReport.doctorAddress?.address ?? null,
    doctorContact: clinicalReport.doctorAddress?.contact ?? null,
    treatingDoctorAddress: clinicalReport.treatingDoctorAddress?.address ?? null,
    treatingDoctorContact: clinicalReport.treatingDoctorAddress?.contact ?? null,
    cantonDoctorAddress: clinicalReport.cantonDoctorAddress?.address ?? null,
    cantonDoctorContact: clinicalReport.cantonDoctorAddress?.contact ?? null,
    notes: clinicalReport.notes ?? '',
  };
}

function getMaterialResultDefaultValue(
  materials: ClinicalReportMaterialDto[],
  testMaterial: ClinicalReportMaterialDtoClinicalTestMaterial,
): MaterialResultFormValues[] {
  const materialResults = materials.find(
    (material) => material.clinicalTestMaterial === testMaterial,
  )?.clinicalReportMaterialResults;

  return [
    {
      clinicalDetectionMethod: ClinicalReportMaterialResultDtoClinicalDetectionMethod.PCR,
      testResult:
        getMaterialResultTestResult(ClinicalReportMaterialResultDtoClinicalDetectionMethod.PCR, materialResults) ??
        null,
    },
    {
      clinicalDetectionMethod: ClinicalReportMaterialResultDtoClinicalDetectionMethod.MICROSCOPY,
      testResult:
        getMaterialResultTestResult(
          ClinicalReportMaterialResultDtoClinicalDetectionMethod.MICROSCOPY,
          materialResults,
        ) ?? null,
    },
    {
      clinicalDetectionMethod: ClinicalReportMaterialResultDtoClinicalDetectionMethod.CULTURE,
      testResult:
        getMaterialResultTestResult(ClinicalReportMaterialResultDtoClinicalDetectionMethod.CULTURE, materialResults) ??
        null,
    },
  ];
}

function getMaterialResultTestResult(
  detectionMethod: ClinicalReportMaterialResultDtoClinicalDetectionMethod,
  materialResults?: ClinicalReportMaterialResultDto[],
) {
  return materialResults?.find((result) => result.clinicalDetectionMethod === detectionMethod)?.testResult;
}
